import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { TELEGRAM_URL } from '../lib/contact';
import { Icon, type IconName } from '../components/Icon';
import { ExamDemo } from '../components/ExamDemo';
import { TryQuestion } from '../components/TryQuestion';
import { TargetMark } from '../components/TargetMark';

/**
 * The page for centres: what Target is, what it does for a centre, and what
 * it costs.
 *
 * The site's front page (and /centres, the address shared with centres). A
 * candidate is one click from sign-in, top right, and arrives through their
 * centre's own link more often than not. Set in the sign-in page's own
 * type — white, black, one red — so the two read as one product.
 *
 * Every claim here is something the platform does today. Nothing here speaks
 * of price: the published terms say Target is free, and plans are added to
 * this page only together with the terms that sell them.
 */

const FEATURES: { icon: IconName; title: string; body: string }[] = [
  {
    icon: 'upload',
    title: 'Your own papers',
    body: 'Import a paper from Word or PDF, or paste it in. Every question can be checked and corrected before a student sees it.',
  },
  {
    icon: 'book',
    title: 'Listening, Reading and Writing',
    body: 'Laid out like the computer-delivered exam, with its timing, its tools and its rules. The recording plays once.',
  },
  {
    icon: 'check',
    title: 'Marked the moment it ends',
    body: 'Listening and Reading come back with a band as soon as they are handed in. "13" and "thirteen" are both accepted.',
  },
  {
    icon: 'pen',
    title: 'Writing for your teachers',
    body: 'Scripts wait for your teachers in one place, and each teacher sees only their own students.',
  },
  {
    icon: 'calendar',
    title: 'Test days',
    body: 'Plan a morning, afternoon or evening session and set the whole test on a class in one go.',
  },
  {
    icon: 'lock',
    title: 'Exam conditions',
    body: 'Full screen, with the keyboard held in Chrome and Edge. Leaving is recorded, and a second time ends the test.',
  },
];

const STEPS: { title: string; body: string }[] = [
  { title: 'Message us', body: 'On Telegram. Tell us the centre’s name.' },
  { title: 'We open your centre', body: 'With your name and colour on its own sign-in page.' },
  { title: 'Set your first test', body: 'Import or choose papers and set them on your classes.' },
  { title: 'Run a test day', body: 'Students sit it on the screen; Listening and Reading come back marked.' },
];

export function CentresPage() {
  useEffect(() => {
    document.title = 'Target for centres';
  }, []);

  /*
   * Each section rises into place the first time it scrolls into view, and
   * then stays. Turned on from here, so a page whose script never ran shows
   * everything at once rather than nothing.
   */
  const page = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const root = page.current;
    if (!root || !('IntersectionObserver' in window)) return undefined;
    root.dataset.reveal = '';
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -12% 0px' },
    );
    root.querySelectorAll('.lp-section').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const telegram = (label: string, primary = false) => (
    <a
      className={`lp-btn${primary ? ' lp-btn--primary' : ''}`}
      href={TELEGRAM_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span>{label}</span>
      {primary && <Icon name="arrowRight" size={18} className="lp-btn__go" />}
    </a>
  );

  return (
    <div className="lp" ref={page}>
      <header className="lp-bar">
        <Link to="/" className="lp-mark" aria-label="Target">
          <TargetMark size={22} className="lp-mark__icon" />
          Target
        </Link>
        <nav className="lp-nav" aria-label="On this page">
          <a href="#features">What you get</a>
          <Link to="/login">Sign in</Link>
        </nav>
      </header>

      <main>
        <section className="lp-hero">
          <div className="lp-hero__text">
            <h1 className="lp-claim">
              Mock exams on the computer,
              <br />
              <span>run by your centre.</span>
            </h1>
            <p className="lp-lede">
              Your students sit Listening, Reading and Writing on the kind of screen they will meet
              on test day. Your teachers set the papers and see the results.
            </p>
            <div className="lp-actions">
              {telegram('Message us to start', true)}
              <Link className="lp-btn" to="/login">
                Sign in
              </Link>
            </div>
            <p className="lp-note">We open your centre’s account ourselves, on Telegram.</p>
          </div>
          <ExamDemo />
        </section>

        <section className="lp-section" id="try">
          <p className="lp-kicker">Try one</p>
          <h2 className="lp-h2">Answer it, and see it marked.</h2>
          <p className="lp-body">
            One question as your students meet it. Choose an answer: it is marked at once, and the
            sentence it came from lights up in the passage.
          </p>
          <TryQuestion />
        </section>

        <section className="lp-section" id="features">
          <p className="lp-kicker">What your centre gets</p>
          <h2 className="lp-h2">Everything a mock exam needs, and nothing it doesn’t.</h2>
          <ul className="lp-features">
            {FEATURES.map((feature) => (
              <li key={feature.title}>
                <span className="lp-features__icon" aria-hidden="true">
                  <Icon name={feature.icon} size={20} />
                </span>
                <h3>{feature.title}</h3>
                <p>{feature.body}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="lp-section lp-split">
          <div>
            <p className="lp-kicker">After the test</p>
            <h2 className="lp-h2">Show them where each answer was.</h2>
            <p className="lp-body">
              When you switch it on, students can go back over a paper they have sat. Each question
              points to the sentence in the passage its answer came from, so a wrong answer becomes
              a lesson rather than just a mark.
            </p>
          </div>
          <figure className="lp-shot">
            <img
              src="/landing/review.webp"
              width={1600}
              height={1000}
              loading="lazy"
              alt="Going back over a Reading paper: the sentence an answer came from is highlighted in the passage."
            />
          </figure>
        </section>

        <section className="lp-section">
          <p className="lp-kicker">How it starts</p>
          <h2 className="lp-h2">From a message to your first test day.</h2>
          <ol className="lp-steps">
            {STEPS.map((step, i) => (
              <li key={step.title}>
                <span className="lp-steps__n">{i + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="lp-section lp-close">
          <h2 className="lp-h2">See it with your own students.</h2>
          <p className="lp-body">
            Message us on Telegram and we will open your centre, with its own sign-in page.
          </p>
          <div className="lp-actions">{telegram('Message us on Telegram', true)}</div>
        </section>
      </main>

      <footer className="lp-foot">
        <p>
          <Link to="/legal">About &amp; legal</Link>. Independent practice, not affiliated with any
          examination board; no qualification is awarded.
        </p>
      </footer>
    </div>
  );
}
